// src/pages/EditGame.js
import React, { useEffect, useState } from 'react';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { useParams, useNavigate } from 'react-router-dom';
import { db } from '../utils/firebase';
import { useAuth } from '../contexts/authContext';
import ImageUploader from '../components/ImageUploader';
import Header from '../components/Header';

const EditGame = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isAdmin, claims, role, loading: userLoading } = useAuth();
  const [gameData, setGameData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sourceCollection, setSourceCollection] = useState('games'); // Track where the game was loaded from

  console.log('🔧 EDIT GAME: Component mounted', { gameId: id, userId: user?.uid });

  // Available categories for suggestions
  const availableCategories = [
    'History', 'Science', 'Geography', 'Sports', 'Entertainment', 
    'Literature', 'Music', 'Movies & TV', 'Technology', 'Art', 
    'Politics', 'Business', 'Food & Drink', 'Nature', 'Math',
    'Pop Culture', 'Mythology', 'Religion', 'Fashion', 'Other'
  ];

  useEffect(() => {
  const fetchGame = async () => {
    console.log('🔧 EDIT GAME: Starting to fetch game', { gameId: id, userId: user?.uid });
    
    if (!user) {
      console.log('🔧 EDIT GAME: No user found');
      setMessage('❌ You must be logged in to edit games.');
      setLoading(false);
      return;
    }
    
    // Wait for user data to load (including admin status)
    if (userLoading) {
      console.log('🔧 EDIT GAME: Waiting for user data to load...');
      return;
    }

      try {
        setLoading(true);
        console.log('🔧 EDIT GAME: Loading game from regular games collection');
        
        // First try regular games collection (personal games)
        const docRef = doc(db, 'games', id);
        const docSnap = await getDoc(docRef);
        
        if (!docSnap.exists()) {
          console.log('🔧 EDIT GAME: Game not found in regular games collection, checking marketplace collection');
          
          // If not found in regular games, check marketplace collection
          const marketplaceDocRef = doc(db, 'games/content/triviaGames', id);
          const marketplaceDocSnap = await getDoc(marketplaceDocRef);
          
          if (!marketplaceDocSnap.exists()) {
            console.log('🔧 EDIT GAME: Game document does not exist in either collection');
            setMessage('❌ Game not found.');
            setLoading(false);
            return;
          }
          
          // Found in marketplace collection
          const data = marketplaceDocSnap.data();
          console.log('🔧 EDIT GAME: Game found in marketplace collection', { 
            gameId: id, 
            title: data.title, 
            creatorId: data.creatorId,
            ownerId: data.ownerId,
            currentUserId: user.uid,
            isForMarketplace: data.isForMarketplace,
            status: data.status,
            marketplaceStatus: data.marketplaceStatus
          });
          
          // Check if user owns this game
          const isOwner = data.creatorId === user.uid || data.ownerId === user.uid;
          console.log('🔧 EDIT GAME: Ownership check', { isOwner, creatorId: data.creatorId, ownerId: data.ownerId, userId: user.uid });
          
          if (!isOwner) {
            console.log('🔧 EDIT GAME: User does not own this game');
            setMessage('❌ You can only edit your own games.');
            setLoading(false);
            return;
          }
          
          // Check if this is a marketplace game (should not be editable by regular users)
          const isMarketplaceGame = data.isForMarketplace === true || data.marketplaceData?.isApproved === true || data.status === 'approved';
          console.log('🔧 EDIT GAME: Marketplace check', { 
            isMarketplaceGame, 
            isForMarketplace: data.isForMarketplace, 
            isApproved: data.marketplaceData?.isApproved, 
            status: data.status 
          });
          
          // Check if user is admin
          console.log('🔧 EDIT GAME: Admin check', { isAdmin, claims, role, userLoading });
          
          if (isMarketplaceGame && !isAdmin) {
            console.log('🔧 EDIT GAME: Game is a marketplace game and user is not admin, blocking edit');
            setMessage('❌ You cannot edit approved marketplace games. This is the marketplace version of your game.');
            setLoading(false);
            return;
          }
          
          console.log('🔧 EDIT GAME: Game is editable, loading into component');
          setGameData(data);
          setSourceCollection('games/content/triviaGames');
          setLoading(false);
          return;
        }

        const data = docSnap.data();
        console.log('🔧 EDIT GAME: Game data loaded', { 
          gameId: id, 
          title: data.title, 
          creatorId: data.creatorId, 
          ownerId: data.ownerId,
          currentUserId: user.uid,
          isForMarketplace: data.isForMarketplace,
          status: data.status,
          marketplaceStatus: data.marketplaceStatus
        });
        
        // Check if user owns this game
        const isOwner = data.creatorId === user.uid || data.ownerId === user.uid;
        console.log('🔧 EDIT GAME: Ownership check', { isOwner, creatorId: data.creatorId, ownerId: data.ownerId, userId: user.uid });
        
        if (!isOwner) {
          console.log('🔧 EDIT GAME: User does not own this game');
          setMessage('❌ You can only edit your own games.');
          setLoading(false);
          return;
        }

        // Check if this is a marketplace game (should not be editable by users)
        // Note: We allow editing if it's the user's personal copy, even if submitted to marketplace
        const isMarketplaceGame = data.isForMarketplace === true || 
                                 data.marketplaceData?.isApproved === true ||
                                 data.status === 'approved';
        
        console.log('🔧 EDIT GAME: Marketplace check', { 
          isMarketplaceGame, 
          isForMarketplace: data.isForMarketplace,
          isApproved: data.marketplaceData?.isApproved,
          status: data.status
        });
        
        // Check if user is admin
        console.log('🔧 EDIT GAME: Admin check', { isAdmin });
        
        if (isMarketplaceGame && !isAdmin) {
          console.log('🔧 EDIT GAME: Game is a marketplace game and user is not admin, blocking edit');
          setMessage('❌ You cannot edit games that have been submitted to the marketplace. Once submitted, games become read-only.');
          setLoading(false);
          return;
        }

        // If the game has been submitted to marketplace, show a warning but allow editing
        if (data.marketplaceStatus === 'submitted' || data.status === 'submitted') {
          console.log('⚠️ EDIT GAME: Editing personal copy of game that has been submitted to marketplace');
        }

        // Game is editable - load it
        console.log('🔧 EDIT GAME: Game is editable, loading into component');
        setGameData(data);
        setSourceCollection('games');
        setLoading(false);
      } catch (error) {
        console.error('🔧 EDIT GAME: Error loading game:', error);
        setMessage('❌ Failed to load game.');
        setLoading(false);
      }
    };

    if (user) {
      fetchGame();
    }
  }, [id, user, userLoading]);

  const handleChange = (section, index, field, value) => {
    const updated = { ...gameData };
    if (Array.isArray(updated[section])) {
      updated[section] = updated[section].map((item, i) =>
        i === index ? { ...item, [field]: value } : item
      );
    } else {
      updated[section][field] = value;
    }
    setGameData(updated);
  };

  const handleSave = async () => {
    if (!user) {
      setMessage('❌ You must be logged in to save games.');
      return;
    }

    setSaving(true);
    try {
      // Save to the same collection where the game was loaded from
      await updateDoc(doc(db, sourceCollection, id), {
        ...gameData,
        updatedAt: new Date(),
        lastEditedBy: user.uid,
        lastEditedAt: new Date()
      });
      
      setMessage('✅ Game updated successfully!');
      setTimeout(() => navigate('/my-games'), 1500);
    } catch (err) {
      console.error('Error saving game:', err);
      setMessage('❌ Failed to update game: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleImageDelete = (section, index, field) => {
    const updated = { ...gameData };
    if (Array.isArray(updated[section])) {
      updated[section][index][field] = '';
    } else {
      updated[section][field] = '';
    }
    setGameData(updated);
  };

  const renderQuarter = (quarter, label) => (
    <div className="mb-8">
      <h2 className="text-xl font-semibold mb-2 text-white">{label}</h2>
      {gameData[quarter].map((q, i) => (
        <div key={i} className="bg-gray-800 p-4 rounded shadow mb-4 space-y-2 border border-gray-600">
          <input
            type="text"
            className="w-full p-2 border rounded bg-gray-700 text-white border-gray-600 placeholder-gray-400"
            placeholder="Category (e.g., History, Science, Pop Culture)"
            value={q.category}
            onChange={(e) => handleChange(quarter, i, 'category', e.target.value)}
            list="category-suggestions"
          />
          <datalist id="category-suggestions">
            {availableCategories.map(cat => (
              <option key={cat} value={cat} />
            ))}
          </datalist>
          <label className="block font-semibold text-white">Question Type</label>
          <select
            className="w-full p-2 border rounded bg-gray-700 text-white border-gray-600"
            value={q.type}
            onChange={(e) => {
              const newType = e.target.value;
              handleChange(quarter, i, 'type', newType);
              
              // Clear old answer format when switching to matching
              if (newType === 'matching') {
                handleChange(quarter, i, 'answer', '');
                handleChange(quarter, i, 'answerPairs', [{}, {}, {}, {}]);
              }
              // Clear old answer format when switching to ordering
              if (newType === 'ordering') {
                handleChange(quarter, i, 'answer', '');
                handleChange(quarter, i, 'orderingAnswer', ['', '', '', '']);
              }
            }}
          >
            <option value="standard">Standard</option>
            <option value="matching">Matching</option>
            <option value="ordering">Ordering</option>
          </select>
          
          {q.type === 'matching' ? (
            <div className="space-y-2">
              <label className="block font-semibold text-sm text-white">Instruction</label>
              <textarea
                className="w-full p-2 border rounded bg-gray-700 text-white border-gray-600 placeholder-gray-400"
                placeholder="e.g., Match the US state to its nickname"
                value={q.questionInstruction || ''}
                onChange={(e) => handleChange(quarter, i, 'questionInstruction', e.target.value)}
              />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <div>
                  <label className="block font-semibold text-sm text-white">Left Column (A, B, C, D...)</label>
                  <textarea
                    className="w-full p-2 border rounded bg-gray-700 text-white border-gray-600 placeholder-gray-400"
                    placeholder="A. Alabama&#10;B. Colorado&#10;C. Idaho&#10;D. North Dakota"
                    value={q.leftColumn || ''}
                    onChange={(e) => handleChange(quarter, i, 'leftColumn', e.target.value)}
                    rows="4"
                  />
                </div>
                <div>
                  <label className="block font-semibold text-sm text-white">Right Column (1, 2, 3, 4...)</label>
                  <textarea
                    className="w-full p-2 border rounded bg-gray-700 text-white border-gray-600 placeholder-gray-400"
                    placeholder="1. The Peace Garden State&#10;2. The Yellowhammer State&#10;3. The Gem State&#10;4. The Centennial State"
                    value={q.rightColumn || ''}
                    onChange={(e) => handleChange(quarter, i, 'rightColumn', e.target.value)}
                    rows="4"
                  />
                </div>
              </div>
            </div>
          ) : q.type === 'ordering' ? (
            <div className="space-y-2">
              <label className="block font-semibold text-sm text-white">Instruction</label>
              <textarea
                className="w-full p-2 border rounded bg-gray-700 text-white border-gray-600 placeholder-gray-400"
                placeholder="e.g., Put the following states in order from largest to smallest by total area"
                value={q.questionInstruction || ''}
                onChange={(e) => handleChange(quarter, i, 'questionInstruction', e.target.value)}
              />
              <label className="block font-semibold text-sm text-white">Items to Order (one per line)</label>
              <textarea
                className="w-full p-2 border rounded bg-gray-700 text-white border-gray-600 placeholder-gray-400"
                placeholder="Georgia&#10;Oregon&#10;Oklahoma&#10;Nebraska"
                value={q.orderingItems || ''}
                onChange={(e) => handleChange(quarter, i, 'orderingItems', e.target.value)}
                rows="4"
              />
            </div>
          ) : (
            <textarea
              className="w-full p-2 border rounded bg-gray-700 text-white border-gray-600 placeholder-gray-400"
              placeholder={`Question ${i + 1}`}
              value={q.question}
              onChange={(e) => handleChange(quarter, i, 'question', e.target.value)}
            />
          )}
          
          <ImageUploader
            label={`Upload Image for Question ${i + 1}`}
            storagePath={`game-images/${gameData.creatorId}/${id}/${quarter}-q${i + 1}-question`}
            onUpload={(url) => handleChange(quarter, i, 'questionImage', url)}
          />
          {q.questionImage && (
            <div className="relative">
              <img src={q.questionImage} alt="" className="max-h-32 rounded border" />
              <button
                className="absolute top-1 right-1 bg-red-600 text-white text-xs px-2 py-1 rounded"
                onClick={() => handleImageDelete(quarter, i, 'questionImage')}
              >
                ✕
              </button>
            </div>
          )}
                    {q.type === 'matching' ? (
            <div className="space-y-2">
              <label className="block font-semibold text-sm text-white">Correct Answer Pairs</label>
              <div className="space-y-2">
                {(q.answerPairs || [{}, {}, {}, {}]).map((pair, pairIndex) => (
                  <div key={pairIndex} className="flex gap-2 items-center">
                    <input
                      type="text"
                      className="flex-1 p-2 border rounded bg-gray-700 text-white border-gray-600 placeholder-gray-400"
                      placeholder={`Left item ${pairIndex + 1}`}
                      value={pair.left || ''}
                      onChange={(e) => {
                        const currentPairs = q.answerPairs || [{}, {}, {}, {}];
                        currentPairs[pairIndex] = { ...currentPairs[pairIndex], left: e.target.value };
                        const formattedAnswer = currentPairs
                          .filter(pair => pair.left && pair.right)
                          .map(pair => `${pair.left} – ${pair.right}`)
                          .join('\n');
                        handleChange(quarter, i, 'answerPairs', currentPairs);
                        handleChange(quarter, i, 'answer', formattedAnswer);
                      }}
                    />
                    <span className="text-white self-center">→</span>
                    <input
                      type="text"
                      className="flex-1 p-2 border rounded bg-gray-700 text-white border-gray-600 placeholder-gray-400"
                      placeholder={`Right item ${pairIndex + 1}`}
                      value={pair.right || ''}
                      onChange={(e) => {
                        const currentPairs = q.answerPairs || [{}, {}, {}, {}];
                        currentPairs[pairIndex] = { ...currentPairs[pairIndex], right: e.target.value };
                        const formattedAnswer = currentPairs
                          .filter(pair => pair.left && pair.right)
                          .map(pair => `${pair.left} – ${pair.right}`)
                          .join('\n');
                        handleChange(quarter, i, 'answerPairs', currentPairs);
                        handleChange(quarter, i, 'answer', formattedAnswer);
                      }}
                    />
                    <button
                      type="button"
                      className="bg-red-600 text-white px-2 py-1 rounded text-sm hover:bg-red-700"
                      onClick={() => {
                        const currentPairs = q.answerPairs || [{}, {}, {}, {}];
                        currentPairs.splice(pairIndex, 1);
                        const formattedAnswer = currentPairs
                          .filter(pair => pair.left && pair.right)
                          .map(pair => `${pair.left} – ${pair.right}`)
                          .join('\n');
                        handleChange(quarter, i, 'answerPairs', currentPairs);
                        handleChange(quarter, i, 'answer', formattedAnswer);
                      }}
                    >
                      ✕
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700"
                  onClick={() => {
                    const currentPairs = q.answerPairs || [{}, {}, {}, {}];
                    currentPairs.push({});
                    handleChange(quarter, i, 'answerPairs', currentPairs);
                  }}
                >
                  + Add Pair
                </button>
              </div>
            </div>
          ) : q.type === 'ordering' ? (
            <div className="space-y-2">
              <label className="block font-semibold text-sm text-white">Correct Order (one per line)</label>
              <div className="space-y-2">
                {(q.orderingAnswer || ['', '', '', '']).map((item, itemIndex) => (
                  <div key={itemIndex} className="flex gap-2 items-center">
                    <span className="text-white font-semibold w-8">{itemIndex + 1}.</span>
                    <input
                      type="text"
                      className="flex-1 p-2 border rounded bg-gray-700 text-white border-gray-600 placeholder-gray-400"
                      placeholder={`Item ${itemIndex + 1}`}
                      value={item}
                      onChange={(e) => {
                        const currentOrder = q.orderingAnswer || ['', '', '', ''];
                        currentOrder[itemIndex] = e.target.value;
                        const formattedAnswer = currentOrder
                          .filter(item => item.trim())
                          .join('\n');
                        handleChange(quarter, i, 'orderingAnswer', currentOrder);
                        handleChange(quarter, i, 'answer', formattedAnswer);
                      }}
                    />
                    <button
                      type="button"
                      className="bg-red-600 text-white px-2 py-1 rounded text-sm hover:bg-red-700"
                      onClick={() => {
                        const currentOrder = q.orderingAnswer || ['', '', '', ''];
                        currentOrder.splice(itemIndex, 1);
                        const formattedAnswer = currentOrder
                          .filter(item => item.trim())
                          .join('\n');
                        handleChange(quarter, i, 'orderingAnswer', currentOrder);
                        handleChange(quarter, i, 'answer', formattedAnswer);
                      }}
                    >
                      ✕
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700"
                  onClick={() => {
                    const currentOrder = q.orderingAnswer || ['', '', '', ''];
                    currentOrder.push('');
                    handleChange(quarter, i, 'orderingAnswer', currentOrder);
                  }}
                >
                  + Add Item
                </button>
              </div>
            </div>
          ) : (
            <input
              type="text"
              className="w-full p-2 border rounded bg-gray-700 text-white border-gray-600 placeholder-gray-400"
              placeholder="Answer"
              value={q.answer}
              onChange={(e) => handleChange(quarter, i, 'answer', e.target.value)}
            />
          )}
          <ImageUploader
            label={`Upload Image for Answer ${i + 1}`}
            storagePath={`game-images/${gameData.creatorId}/${id}/${quarter}-q${i + 1}-answer`}
            onUpload={(url) => handleChange(quarter, i, 'answerImage', url)}
          />
          {q.answerImage && (
            <div className="relative">
              <img src={q.answerImage} alt="" className="max-h-32 rounded border" />
              <button
                className="absolute top-1 right-1 bg-red-600 text-white text-xs px-2 py-1 rounded"
                onClick={() => handleImageDelete(quarter, i, 'answerImage')}
              >
                ✕
              </button>
            </div>
          )}
          
          {/* Creator Comment for this question */}
          <div className="mt-4">
            <label className="block font-semibold text-white mb-2">
              💡 Creator Comment (Optional)
            </label>
            <textarea
              className="w-full p-2 border rounded bg-gray-700 text-white border-gray-600 placeholder-gray-400"
              placeholder="Explain hints, song clues, or provide context for this specific question..."
              value={q.creatorComment || ''}
              onChange={(e) => handleChange(quarter, i, 'creatorComment', e.target.value)}
              rows="3"
            />
            <p className="text-sm text-gray-400 mt-1">
              Optional: Explain how song hints relate to the answer, provide context, or share inside jokes
            </p>
          </div>
        </div>
      ))}
    </div>
  );

  const renderAdUploader = (label, key) => (
    <div className="mb-8">
      <h2 className="text-xl font-semibold mb-2">{label}</h2>
      <ImageUploader
        label="Upload Ad Images"
        multiple
        onUpload={(urls) =>
          setGameData((prev) => ({
            ...prev,
            [key]: [...(prev[key] || []), ...urls],
          }))
        }
      />
      <div className="flex flex-wrap gap-4 mt-2">
        {gameData[key]?.map((url, i) => (
          <div key={i} className="relative">
            <img src={url} alt={`Ad ${i + 1}`} className="max-h-32 rounded border" />
            <button
              className="absolute top-1 right-1 bg-red-600 text-white text-xs px-2 py-1 rounded"
              onClick={() =>
                setGameData((prev) => ({
                  ...prev,
                  [key]: prev[key].filter((_, index) => index !== i),
                }))
              }
            >
              ✕
            </button>
          </div>
        ))}
      </div>
    </div>
  );

  if (loading) return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center">
      <div className="text-white">Loading game...</div>
    </div>
  );

  if (message && !gameData) return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center">
      <div className="text-center">
        <div className="text-red-400 text-lg mb-4">{message}</div>
        <button 
          onClick={() => navigate('/my-games')}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg"
        >
          Back to My Games
        </button>
      </div>
    </div>
  );

  if (!gameData) return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center">
      <div className="text-white">Game not found.</div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      <Header />
      <div className="p-8">
        <h1 className="text-3xl font-bold mb-6 text-white">✏️ Edit Trivia Game</h1>
        
        {/* Warning for games submitted to marketplace */}
        {(gameData.marketplaceStatus === 'submitted' || gameData.status === 'submitted') && (
          <div className="bg-yellow-900/20 border border-yellow-600/30 text-yellow-200 rounded-lg p-4 mb-6">
            <div className="flex items-center">
              <span className="text-yellow-400 mr-2">⚠️</span>
              <div>
                <p className="font-semibold">This game has been submitted to the marketplace</p>
                <p className="text-sm text-yellow-300">
                  You're editing your personal copy. Changes here won't affect the marketplace version.
                </p>
              </div>
            </div>
          </div>
        )}

      {renderQuarter('quarter1', 'Quarter 1')}
      {renderQuarter('quarter2', 'Quarter 2')}

      <div className="mb-8">
        <h2 className="text-xl font-semibold mb-2 text-white">⏸️ Halftime</h2>
        <div className="bg-gray-800 p-4 rounded shadow border border-gray-600 space-y-2">
        <input
          className="w-full p-2 border rounded mb-2 bg-gray-700 text-white border-gray-600 placeholder-gray-400"
          placeholder="Halftime Category (e.g., History, Science, Pop Culture)"
          value={gameData.halftime.category}
          onChange={(e) => handleChange('halftime', null, 'category', e.target.value)}
          list="category-suggestions"
        />
        <datalist id="category-suggestions">
          {availableCategories.map(cat => (
            <option key={cat} value={cat} />
          ))}
        </datalist>
        <label className="block font-semibold mt-2">Question Type</label>
        <select
          className="w-full p-2 border rounded bg-gray-700 text-white border-gray-600"
          value={gameData.halftime.type}
          onChange={(e) => {
            const newType = e.target.value;
            handleChange('halftime', null, 'type', newType);
            
            // Clear old answer format when switching to matching
            if (newType === 'matching') {
              handleChange('halftime', null, 'answer', '');
              handleChange('halftime', null, 'answerPairs', [{}, {}, {}, {}]);
            }
            // Clear old answer format when switching to ordering
            if (newType === 'ordering') {
              handleChange('halftime', null, 'answer', '');
              handleChange('halftime', null, 'orderingAnswer', ['', '', '', '']);
            }
          }}
        >
          <option value="standard">Standard</option>
          <option value="matching">Matching</option>
          <option value="ordering">Ordering</option>
        </select>
        
        {gameData.halftime.type === 'matching' ? (
          <div className="space-y-2 mt-2">
            <label className="block font-semibold text-sm">Instruction</label>
            <textarea
              className="w-full p-2 border rounded bg-gray-700 text-white border-gray-600 placeholder-gray-400"
              placeholder="e.g., Match the US state to its nickname"
              value={gameData.halftime.questionInstruction || ''}
              onChange={(e) => handleChange('halftime', null, 'questionInstruction', e.target.value)}
            />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <div>
                <label className="block font-semibold text-sm">Left Column (A, B, C, D...)</label>
                <textarea
                  className="w-full p-2 border rounded bg-gray-700 text-white border-gray-600 placeholder-gray-400"
                  placeholder="A. Alabama&#10;B. Colorado&#10;C. Idaho&#10;D. North Dakota"
                  value={gameData.halftime.leftColumn || ''}
                  onChange={(e) => handleChange('halftime', null, 'leftColumn', e.target.value)}
                  rows="4"
                />
              </div>
              <div>
                <label className="block font-semibold text-sm">Right Column (1, 2, 3, 4...)</label>
                <textarea
                  className="w-full p-2 border rounded bg-gray-700 text-white border-gray-600 placeholder-gray-400"
                  placeholder="1. The Peace Garden State&#10;2. The Yellowhammer State&#10;3. The Gem State&#10;4. The Centennial State"
                  value={gameData.halftime.rightColumn || ''}
                  onChange={(e) => handleChange('halftime', null, 'rightColumn', e.target.value)}
                  rows="4"
                />
              </div>
            </div>
          </div>
        ) : gameData.halftime.type === 'ordering' ? (
          <div className="space-y-2 mt-2">
            <label className="block font-semibold text-sm">Instruction</label>
            <textarea
              className="w-full p-2 border rounded bg-gray-700 text-white border-gray-600 placeholder-gray-400"
              placeholder="e.g., Put the following states in order from largest to smallest by total area"
              value={gameData.halftime.questionInstruction || ''}
              onChange={(e) => handleChange('halftime', null, 'questionInstruction', e.target.value)}
            />
            <label className="block font-semibold text-sm">Items to Order (one per line)</label>
            <textarea
              className="w-full p-2 border rounded bg-gray-700 text-white border-gray-600 placeholder-gray-400"
              placeholder="Georgia&#10;Oregon&#10;Oklahoma&#10;Nebraska"
              value={gameData.halftime.orderingItems || ''}
              onChange={(e) => handleChange('halftime', null, 'orderingItems', e.target.value)}
              rows="4"
            />
          </div>
        ) : (
          <textarea
            className="w-full p-2 border rounded mb-2 bg-gray-700 text-white border-gray-600 placeholder-gray-400"
            placeholder="Halftime Question"
            value={gameData.halftime.question}
            onChange={(e) => handleChange('halftime', null, 'question', e.target.value)}
          />
        )}
        
        {gameData.halftime.type === 'matching' ? (
          <div className="space-y-2">
            <label className="block font-semibold text-sm text-white">Correct Answer Pairs</label>
            <div className="space-y-2">
              {(gameData.halftime.answerPairs || [{}, {}, {}, {}]).map((pair, pairIndex) => (
                <div key={pairIndex} className="flex gap-2 items-center">
                  <input
                    type="text"
                    className="flex-1 p-2 border rounded bg-gray-700 text-white border-gray-600 placeholder-gray-400"
                    placeholder={`Left item ${pairIndex + 1}`}
                    value={pair.left || ''}
                    onChange={(e) => {
                      const currentPairs = gameData.halftime.answerPairs || [{}, {}, {}, {}];
                      currentPairs[pairIndex] = { ...currentPairs[pairIndex], left: e.target.value };
                      const formattedAnswer = currentPairs
                        .filter(pair => pair.left && pair.right)
                        .map(pair => `${pair.left} – ${pair.right}`)
                        .join('\n');
                      handleChange('halftime', null, 'answerPairs', currentPairs);
                      handleChange('halftime', null, 'answer', formattedAnswer);
                    }}
                  />
                  <span className="text-white self-center">→</span>
                  <input
                    type="text"
                    className="flex-1 p-2 border rounded bg-gray-700 text-white border-gray-600 placeholder-gray-400"
                    placeholder={`Right item ${pairIndex + 1}`}
                    value={pair.right || ''}
                    onChange={(e) => {
                      const currentPairs = gameData.halftime.answerPairs || [{}, {}, {}, {}];
                      currentPairs[pairIndex] = { ...currentPairs[pairIndex], right: e.target.value };
                      const formattedAnswer = currentPairs
                        .filter(pair => pair.left && pair.right)
                        .map(pair => `${pair.left} – ${pair.right}`)
                        .join('\n');
                      handleChange('halftime', null, 'answerPairs', currentPairs);
                      handleChange('halftime', null, 'answer', formattedAnswer);
                    }}
                  />
                  <button
                    type="button"
                    className="bg-red-600 text-white px-2 py-1 rounded text-sm hover:bg-red-700"
                    onClick={() => {
                      const currentPairs = gameData.halftime.answerPairs || [{}, {}, {}, {}];
                      currentPairs.splice(pairIndex, 1);
                      const formattedAnswer = currentPairs
                        .filter(pair => pair.left && pair.right)
                        .map(pair => `${pair.left} – ${pair.right}`)
                        .join('\n');
                      handleChange('halftime', null, 'answerPairs', currentPairs);
                      handleChange('halftime', null, 'answer', formattedAnswer);
                    }}
                  >
                    ✕
                  </button>
                </div>
              ))}
              <button
                type="button"
                className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700"
                onClick={() => {
                  const currentPairs = gameData.halftime.answerPairs || [{}, {}, {}, {}];
                  currentPairs.push({});
                  handleChange('halftime', null, 'answerPairs', currentPairs);
                }}
              >
                + Add Pair
              </button>
            </div>
          </div>
        ) : gameData.halftime.type === 'ordering' ? (
          <div className="space-y-2">
            <label className="block font-semibold text-sm text-white">Correct Order (one per line)</label>
            <div className="space-y-2">
              {(gameData.halftime.orderingAnswer || ['', '', '', '']).map((item, itemIndex) => (
                <div key={itemIndex} className="flex gap-2 items-center">
                  <span className="text-white font-semibold w-8">{itemIndex + 1}.</span>
                  <input
                    type="text"
                    className="flex-1 p-2 border rounded bg-gray-700 text-white border-gray-600 placeholder-gray-400"
                    placeholder={`Item ${itemIndex + 1}`}
                    value={item}
                    onChange={(e) => {
                      const currentOrder = gameData.halftime.orderingAnswer || ['', '', '', ''];
                      currentOrder[itemIndex] = e.target.value;
                      const formattedAnswer = currentOrder
                        .filter(item => item.trim())
                        .join('\n');
                      handleChange('halftime', null, 'orderingAnswer', currentOrder);
                      handleChange('halftime', null, 'answer', formattedAnswer);
                    }}
                  />
                  <button
                    type="button"
                    className="bg-red-600 text-white px-2 py-1 rounded text-sm hover:bg-red-700"
                    onClick={() => {
                      const currentOrder = gameData.halftime.orderingAnswer || ['', '', '', ''];
                      currentOrder.splice(itemIndex, 1);
                      const formattedAnswer = currentOrder
                        .filter(item => item.trim())
                        .join('\n');
                      handleChange('halftime', null, 'orderingAnswer', currentOrder);
                      handleChange('halftime', null, 'answer', formattedAnswer);
                    }}
                  >
                    ✕
                  </button>
                </div>
              ))}
              <button
                type="button"
                className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700"
                onClick={() => {
                  const currentOrder = gameData.halftime.orderingAnswer || ['', '', '', ''];
                  currentOrder.push('');
                  handleChange('halftime', null, 'orderingAnswer', currentOrder);
                }}
              >
                + Add Item
              </button>
            </div>
          </div>
        ) : (
          <input
            className="w-full p-2 border rounded bg-gray-700 text-white border-gray-600 placeholder-gray-400"
            placeholder="Halftime Answer"
            value={gameData.halftime.answer}
            onChange={(e) => handleChange('halftime', null, 'answer', e.target.value)}
          />
        )}
        
        {/* Creator Comment for Halftime */}
        <div className="mt-4">
          <label className="block font-semibold text-white mb-2">
            💡 Creator Comment (Optional)
          </label>
          <textarea
            className="w-full p-2 border rounded bg-gray-700 text-white border-gray-600 placeholder-gray-400"
            placeholder="Explain hints, song clues, or provide context for this specific question..."
            value={gameData.halftime.creatorComment || ''}
            onChange={(e) => handleChange('halftime', null, 'creatorComment', e.target.value)}
            rows="3"
          />
          <p className="text-sm text-gray-400 mt-1">
            Optional: Explain how song hints relate to the answer, provide context, or share inside jokes
          </p>
        </div>
        </div>
      </div>

      {renderQuarter('quarter3', 'Quarter 3')}
      {renderQuarter('quarter4', 'Quarter 4')}

      <div className="mb-8">
        <h2 className="text-xl font-semibold mb-2 text-white">🎯 Final Question</h2>
        <div className="bg-gray-800 p-4 rounded shadow border border-gray-600 space-y-2">
        <input
          className="w-full p-2 border rounded mb-2 bg-gray-700 text-white border-gray-600 placeholder-gray-400"
          placeholder="Final Question Category (e.g., History, Science, Pop Culture)"
          value={gameData.final.category}
          onChange={(e) => handleChange('final', null, 'category', e.target.value)}
          list="category-suggestions"
        />
        <datalist id="category-suggestions">
          {availableCategories.map(cat => (
            <option key={cat} value={cat} />
          ))}
        </datalist>
        <label className="block font-semibold mt-2">Question Type</label>
        <select
          className="w-full p-2 border rounded bg-gray-700 text-white border-gray-600"
          value={gameData.final.type}
          onChange={(e) => {
            const newType = e.target.value;
            handleChange('final', null, 'type', newType);
            
            // Clear old answer format when switching to matching
            if (newType === 'matching') {
              handleChange('final', null, 'answer', '');
              handleChange('final', null, 'answerPairs', [{}, {}, {}, {}]);
            }
            // Clear old answer format when switching to ordering
            if (newType === 'ordering') {
              handleChange('final', null, 'answer', '');
              handleChange('final', null, 'orderingAnswer', ['', '', '', '']);
            }
          }}
        >
          <option value="standard">Standard</option>
          <option value="matching">Matching</option>
          <option value="ordering">Ordering</option>
        </select>
        
        {gameData.final.type === 'matching' ? (
          <div className="space-y-2 mt-2">
            <label className="block font-semibold text-sm">Instruction</label>
            <textarea
              className="w-full p-2 border rounded bg-gray-700 text-white border-gray-600 placeholder-gray-400"
              placeholder="e.g., Match the US state to its nickname"
              value={gameData.final.questionInstruction || ''}
              onChange={(e) => handleChange('final', null, 'questionInstruction', e.target.value)}
            />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <div>
                <label className="block font-semibold text-sm">Left Column (A, B, C, D...)</label>
                <textarea
                  className="w-full p-2 border rounded bg-gray-700 text-white border-gray-600 placeholder-gray-400"
                  placeholder="A. Alabama&#10;B. Colorado&#10;C. Idaho&#10;D. North Dakota"
                  value={gameData.final.leftColumn || ''}
                  onChange={(e) => handleChange('final', null, 'leftColumn', e.target.value)}
                  rows="4"
                />
              </div>
              <div>
                <label className="block font-semibold text-sm">Right Column (1, 2, 3, 4...)</label>
                <textarea
                  className="w-full p-2 border rounded bg-gray-700 text-white border-gray-600 placeholder-gray-400"
                  placeholder="1. The Peace Garden State&#10;2. The Yellowhammer State&#10;3. The Gem State&#10;4. The Centennial State"
                  value={gameData.final.rightColumn || ''}
                  onChange={(e) => handleChange('final', null, 'rightColumn', e.target.value)}
                  rows="4"
                />
              </div>
            </div>
          </div>
        ) : gameData.final.type === 'ordering' ? (
          <div className="space-y-2 mt-2">
            <label className="block font-semibold text-sm">Instruction</label>
            <textarea
              className="w-full p-2 border rounded bg-gray-700 text-white border-gray-600 placeholder-gray-400"
              placeholder="e.g., Put the following states in order from largest to smallest by total area"
              value={gameData.final.questionInstruction || ''}
              onChange={(e) => handleChange('final', null, 'questionInstruction', e.target.value)}
            />
            <label className="block font-semibold text-sm">Items to Order (one per line)</label>
            <textarea
              className="w-full p-2 border rounded bg-gray-700 text-white border-gray-600 placeholder-gray-400"
              placeholder="Georgia&#10;Oregon&#10;Oklahoma&#10;Nebraska"
              value={gameData.final.orderingItems || ''}
              onChange={(e) => handleChange('final', null, 'orderingItems', e.target.value)}
              rows="4"
            />
          </div>
        ) : (
          <textarea
            className="w-full p-2 border rounded mb-2 bg-gray-700 text-white border-gray-600 placeholder-gray-400"
            placeholder="Final Question"
            value={gameData.final.question}
            onChange={(e) => handleChange('final', null, 'question', e.target.value)}
          />
        )}
        
        {gameData.final.type === 'matching' ? (
          <div className="space-y-2">
            <label className="block font-semibold text-sm text-white">Correct Answer Pairs</label>
            <div className="space-y-2">
              {(gameData.final.answerPairs || [{}, {}, {}, {}]).map((pair, pairIndex) => (
                <div key={pairIndex} className="flex gap-2 items-center">
                  <input
                    type="text"
                    className="flex-1 p-2 border rounded bg-gray-700 text-white border-gray-600 placeholder-gray-400"
                    placeholder={`Left item ${pairIndex + 1}`}
                    value={pair.left || ''}
                    onChange={(e) => {
                      const currentPairs = gameData.final.answerPairs || [{}, {}, {}, {}];
                      currentPairs[pairIndex] = { ...currentPairs[pairIndex], left: e.target.value };
                      const formattedAnswer = currentPairs
                        .filter(pair => pair.left && pair.right)
                        .map(pair => `${pair.left} – ${pair.right}`)
                        .join('\n');
                      handleChange('final', null, 'answerPairs', currentPairs);
                      handleChange('final', null, 'answer', formattedAnswer);
                    }}
                  />
                  <span className="text-white self-center">→</span>
                  <input
                    type="text"
                    className="flex-1 p-2 border rounded bg-gray-700 text-white border-gray-600 placeholder-gray-400"
                    placeholder={`Right item ${pairIndex + 1}`}
                    value={pair.right || ''}
                    onChange={(e) => {
                      const currentPairs = gameData.final.answerPairs || [{}, {}, {}, {}];
                      currentPairs[pairIndex] = { ...currentPairs[pairIndex], right: e.target.value };
                      const formattedAnswer = currentPairs
                        .filter(pair => pair.left && pair.right)
                        .map(pair => `${pair.left} – ${pair.right}`)
                        .join('\n');
                      handleChange('final', null, 'answerPairs', currentPairs);
                      handleChange('final', null, 'answer', formattedAnswer);
                    }}
                  />
                  <button
                    type="button"
                    className="bg-red-600 text-white px-2 py-1 rounded text-sm hover:bg-red-700"
                    onClick={() => {
                      const currentPairs = gameData.final.answerPairs || [{}, {}, {}, {}];
                      currentPairs.splice(pairIndex, 1);
                      const formattedAnswer = currentPairs
                        .filter(pair => pair.left && pair.right)
                        .map(pair => `${pair.left} – ${pair.right}`)
                        .join('\n');
                      handleChange('final', null, 'answerPairs', currentPairs);
                      handleChange('final', null, 'answer', formattedAnswer);
                    }}
                  >
                    ✕
                  </button>
                </div>
              ))}
              <button
                type="button"
                className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700"
                onClick={() => {
                  const currentPairs = gameData.final.answerPairs || [{}, {}, {}, {}];
                  currentPairs.push({});
                  handleChange('final', null, 'answerPairs', currentPairs);
                }}
              >
                + Add Pair
              </button>
            </div>
          </div>
        ) : gameData.final.type === 'ordering' ? (
          <div className="space-y-2">
            <label className="block font-semibold text-sm text-white">Correct Order (one per line)</label>
            <div className="space-y-2">
              {(gameData.final.orderingAnswer || ['', '', '', '']).map((item, itemIndex) => (
                <div key={itemIndex} className="flex gap-2 items-center">
                  <span className="text-white font-semibold w-8">{itemIndex + 1}.</span>
                  <input
                    type="text"
                    className="flex-1 p-2 border rounded bg-gray-700 text-white border-gray-600 placeholder-gray-400"
                    placeholder={`Item ${itemIndex + 1}`}
                    value={item}
                    onChange={(e) => {
                      const currentOrder = gameData.final.orderingAnswer || ['', '', '', ''];
                      currentOrder[itemIndex] = e.target.value;
                      const formattedAnswer = currentOrder
                        .filter(item => item.trim())
                        .join('\n');
                      handleChange('final', null, 'orderingAnswer', currentOrder);
                      handleChange('final', null, 'answer', formattedAnswer);
                    }}
                  />
                  <button
                    type="button"
                    className="bg-red-600 text-white px-2 py-1 rounded text-sm hover:bg-red-700"
                    onClick={() => {
                      const currentOrder = gameData.final.orderingAnswer || ['', '', '', ''];
                      currentOrder.splice(itemIndex, 1);
                      const formattedAnswer = currentOrder
                        .filter(item => item.trim())
                        .join('\n');
                      handleChange('final', null, 'orderingAnswer', currentOrder);
                      handleChange('final', null, 'answer', formattedAnswer);
                    }}
                  >
                    ✕
                  </button>
                </div>
              ))}
              <button
                type="button"
                className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700"
                onClick={() => {
                  const currentOrder = gameData.final.orderingAnswer || ['', '', '', ''];
                  currentOrder.push('');
                  handleChange('final', null, 'orderingAnswer', currentOrder);
                }}
              >
                + Add Item
              </button>
            </div>
          </div>
        ) : (
          <input
            className="w-full p-2 border rounded bg-gray-700 text-white border-gray-600 placeholder-gray-400"
            placeholder="Final Answer"
            value={gameData.final.answer}
            onChange={(e) => handleChange('final', null, 'answer', e.target.value)}
          />
        )}
        
        {/* Creator Comment for Final */}
        <div className="mt-4">
          <label className="block font-semibold text-white mb-2">
            💡 Creator Comment (Optional)
          </label>
          <textarea
            className="w-full p-2 border rounded bg-gray-700 text-white border-gray-600 placeholder-gray-400"
            placeholder="Explain hints, song clues, or provide context for this specific question..."
            value={gameData.final.creatorComment || ''}
            onChange={(e) => handleChange('final', null, 'creatorComment', e.target.value)}
            rows="3"
          />
          <p className="text-sm text-gray-400 mt-1">
            Optional: Explain how song hints relate to the answer, provide context, or share inside jokes
          </p>
        </div>
        </div>
      </div>



      <div className="mb-8">
        <h2 className="text-xl font-semibold mb-2 text-white">🏁 Tie Breaker</h2>
        <div className="bg-gray-800 p-4 rounded shadow border border-gray-600 space-y-2">
        <input
          className="w-full p-2 border rounded mb-2 bg-gray-700 text-white border-gray-600 placeholder-gray-400"
          placeholder="Tie Breaker Category (e.g., History, Science, Pop Culture)"
          value={gameData.tieBreaker.category}
          onChange={(e) => handleChange('tieBreaker', null, 'category', e.target.value)}
          list="category-suggestions"
        />
        <datalist id="category-suggestions">
          {availableCategories.map(cat => (
            <option key={cat} value={cat} />
          ))}
        </datalist>
        <label className="block font-semibold mt-2">Question Type</label>
        <select
          className="w-full p-2 border rounded bg-gray-700 text-white border-gray-600"
          value={gameData.tieBreaker.type}
          onChange={(e) => {
            const newType = e.target.value;
            handleChange('tieBreaker', null, 'type', newType);
            
            // Clear old answer format when switching to matching
            if (newType === 'matching') {
              handleChange('tieBreaker', null, 'answer', '');
              handleChange('tieBreaker', null, 'answerPairs', [{}, {}, {}, {}]);
            }
          }}
        >
          <option value="standard">Standard</option>
          <option value="matching">Matching</option>
          <option value="ordering">Ordering</option>
        </select>
        
        {gameData.tieBreaker.type === 'matching' ? (
          <div className="space-y-2 mt-2">
            <label className="block font-semibold text-sm">Instruction</label>
            <textarea
              className="w-full p-2 border rounded bg-gray-700 text-white border-gray-600 placeholder-gray-400"
              placeholder="e.g., Match the US state to its nickname"
              value={gameData.tieBreaker.questionInstruction || ''}
              onChange={(e) => handleChange('tieBreaker', null, 'questionInstruction', e.target.value)}
            />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <div>
                <label className="block font-semibold text-sm">Left Column (A, B, C, D...)</label>
                <textarea
                  className="w-full p-2 border rounded bg-gray-700 text-white border-gray-600 placeholder-gray-400"
                  placeholder="A. Alabama&#10;B. Colorado&#10;C. Idaho&#10;D. North Dakota"
                  value={gameData.tieBreaker.leftColumn || ''}
                  onChange={(e) => handleChange('tieBreaker', null, 'leftColumn', e.target.value)}
                  rows="4"
                />
              </div>
              <div>
                <label className="block font-semibold text-sm">Right Column (1, 2, 3, 4...)</label>
                <textarea
                  className="w-full p-2 border rounded bg-gray-700 text-white border-gray-600 placeholder-gray-400"
                  placeholder="1. The Peace Garden State&#10;2. The Yellowhammer State&#10;3. The Gem State&#10;4. The Centennial State"
                  value={gameData.tieBreaker.rightColumn || ''}
                  onChange={(e) => handleChange('tieBreaker', null, 'rightColumn', e.target.value)}
                  rows="4"
                />
              </div>
            </div>
          </div>
        ) : (
          <textarea
            className="w-full p-2 border rounded mb-2 bg-gray-700 text-white border-gray-600 placeholder-gray-400"
            placeholder="Tie Breaker Question"
            value={gameData.tieBreaker.question}
            onChange={(e) => handleChange('tieBreaker', null, 'question', e.target.value)}
          />
        )}
        
        {gameData.tieBreaker.type === 'matching' ? (
          <div className="space-y-2">
            <label className="block font-semibold text-sm text-white">Correct Answer Pairs</label>
            <div className="space-y-2">
              {(gameData.tieBreaker.answerPairs || [{}, {}, {}, {}]).map((pair, pairIndex) => (
                <div key={pairIndex} className="flex gap-2 items-center">
                  <input
                    type="text"
                    className="flex-1 p-2 border rounded bg-gray-700 text-white border-gray-600 placeholder-gray-400"
                    placeholder={`Left item ${pairIndex + 1}`}
                    value={pair.left || ''}
                    onChange={(e) => {
                      const currentPairs = gameData.tieBreaker.answerPairs || [{}, {}, {}, {}];
                      currentPairs[pairIndex] = { ...currentPairs[pairIndex], left: e.target.value };
                      const formattedAnswer = currentPairs
                        .filter(pair => pair.left && pair.right)
                        .map(pair => `${pair.left} – ${pair.right}`)
                        .join('\n');
                      handleChange('tieBreaker', null, 'answerPairs', currentPairs);
                      handleChange('tieBreaker', null, 'answer', formattedAnswer);
                    }}
                  />
                  <span className="text-white self-center">→</span>
                  <input
                    type="text"
                    className="flex-1 p-2 border rounded bg-gray-700 text-white border-gray-600 placeholder-gray-400"
                    placeholder={`Right item ${pairIndex + 1}`}
                    value={pair.right || ''}
                    onChange={(e) => {
                      const currentPairs = gameData.tieBreaker.answerPairs || [{}, {}, {}, {}];
                      currentPairs[pairIndex] = { ...currentPairs[pairIndex], right: e.target.value };
                      const formattedAnswer = currentPairs
                        .filter(pair => pair.left && pair.right)
                        .map(pair => `${pair.left} – ${pair.right}`)
                        .join('\n');
                      handleChange('tieBreaker', null, 'answerPairs', currentPairs);
                      handleChange('tieBreaker', null, 'answer', formattedAnswer);
                    }}
                  />
                  <button
                    type="button"
                    className="bg-red-600 text-white px-2 py-1 rounded text-sm hover:bg-red-700"
                    onClick={() => {
                      const currentPairs = gameData.tieBreaker.answerPairs || [{}, {}, {}, {}];
                      currentPairs.splice(pairIndex, 1);
                      const formattedAnswer = currentPairs
                        .filter(pair => pair.left && pair.right)
                        .map(pair => `${pair.left} – ${pair.right}`)
                        .join('\n');
                      handleChange('tieBreaker', null, 'answerPairs', currentPairs);
                      handleChange('tieBreaker', null, 'answer', formattedAnswer);
                    }}
                  >
                    ✕
                  </button>
                </div>
              ))}
              <button
                type="button"
                className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700"
                onClick={() => {
                  const currentPairs = gameData.tieBreaker.answerPairs || [{}, {}, {}, {}];
                  currentPairs.push({});
                  handleChange('tieBreaker', null, 'answerPairs', currentPairs);
                }}
              >
                + Add Pair
              </button>
            </div>
          </div>
        ) : (
          <input
            className="w-full p-2 border rounded bg-gray-700 text-white border-gray-600 placeholder-gray-400"
            placeholder="Tie Breaker Answer"
            value={gameData.tieBreaker.answer}
            onChange={(e) => handleChange('tieBreaker', null, 'answer', e.target.value)}
          />
        )}
        
        {/* Creator Comment for Tiebreaker */}
        <div className="mt-4">
          <label className="block font-semibold text-white mb-2">
            💡 Creator Comment (Optional)
          </label>
          <textarea
            className="w-full p-2 border rounded bg-gray-700 text-white border-gray-600 placeholder-gray-400"
            placeholder="Explain hints, song clues, or provide context for this specific question..."
            value={gameData.tieBreaker.creatorComment || ''}
            onChange={(e) => handleChange('tieBreaker', null, 'creatorComment', e.target.value)}
            rows="3"
          />
          <p className="text-sm text-gray-400 mt-1">
            Optional: Explain how song hints relate to the answer, provide context, or share inside jokes
          </p>
        </div>
        </div>
      </div>

      <div className="flex items-center mt-6">
        <button
          onClick={handleSave}
          disabled={saving}
          className="bg-green-600 text-white px-6 py-3 rounded hover:bg-green-700 transition"
        >
          {saving ? 'Saving...' : '💾 Save Changes'}
        </button>
        <button
          onClick={() => navigate('/admin')}
          className="ml-4 bg-red-600 text-white px-6 py-3 rounded hover:bg-red-700 transition"
        >
          ❌ Cancel
        </button>
      </div>

      {message && <p className="mt-4 text-lg">{message}</p>}
      </div>
    </div>
  );
};

export default EditGame;
